/**
 * Rows of fields parsed from CSV text, in file order.
 *
 * Follows RFC 4180: a field wrapped in double quotes may contain commas, newlines, and doubled
 * quotes (`""`) standing for one literal quote. Outside quotes, `\r\n`, `\n`, and `\r` all end a
 * row, so one file may mix line endings. A leading byte order mark is stripped, and blank lines
 * produce no row at all — including the one a trailing newline would otherwise leave behind.
 *
 * The first row is the header by convention; this function does not treat it specially.
 *
 * Two things it deliberately does not do:
 *
 * - **Fields are not trimmed.** Trimming is the caller's decision; doing it here would corrupt a
 *   deliberately padded quoted value.
 * - **Rows are not padded.** A row shorter than the header keeps its own length, so a caller
 *   indexing a column has to tolerate a missing cell.
 *
 * Returns an empty array for empty or whitespace-only input.
 */
export function parseCsv(text: string): string[][] {
	const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
	if (source.trim() === '') {
		return [];
	}

	const rows: string[][] = [];
	let row: string[] = [];
	let field = '';
	let inQuotes = false;
	// Whether anything has been read since the last row break. A row break with nothing behind it is a
	// blank line, which yields no row — this is what keeps a trailing newline from adding an empty one.
	let rowHasContent = false;

	const endField = (): void => {
		row.push(field);
		field = '';
	};
	const endRow = (): void => {
		endField();
		rows.push(row);
		row = [];
		rowHasContent = false;
	};

	for (let index = 0; index < source.length; index += 1) {
		const char = source[index];

		if (inQuotes) {
			if (char !== '"') {
				field += char;
			} else if (source[index + 1] === '"') {
				// A doubled quote inside a quoted field is one literal quote.
				field += '"';
				index += 1;
			} else {
				inQuotes = false;
			}
			rowHasContent = true;
			continue;
		}

		if (char === '"') {
			inQuotes = true;
			rowHasContent = true;
		} else if (char === ',') {
			endField();
			rowHasContent = true;
		} else if (char === '\n') {
			if (rowHasContent) {
				endRow();
			}
		} else if (char === '\r') {
			// Swallow the \n of a \r\n pair so the pair ends one row rather than two.
			if (source[index + 1] === '\n') {
				index += 1;
			}
			if (rowHasContent) {
				endRow();
			}
		} else {
			field += char;
			rowHasContent = true;
		}
	}

	if (rowHasContent) {
		endRow();
	}

	return rows;
}
